import { AxiosDocumentDownloadInstance } from '@/lib/axios/axios';
import { notification } from 'antd';
import { COOKIE_KEY, FILE_SIZE, LocalStorageKeys, WALLET_AMOUNT_THRESHOLD_COLORS } from '@shared/constants';
import Cookies from 'js-cookie';
import dayjs from 'dayjs';
import { appPublicRoutes, basePublicRoutes } from '@pages/routes/route-public';
import { CallPhoneNumber } from '@modules/phone/types/store/phone.types';
import { PhoneNumberParser } from './phone-number-parser';
import { cookieSubdomain } from './cookie-sub-domain';
import { broadcastAuthChange } from '@shared/hooks/use-auth-state-change';
import { DEVICE_THEME_PREFERENCE_KEY } from '@shared/store/theme.store';

export const toAppFixed = (percent: number) => parseFloat(parseFloat(percent + '').toFixed(2));

export function handleDownload({ url = '', name = 'calilio', ext = '.pdf' }) {
  AxiosDocumentDownloadInstance.post(url).then((res) => {
    // Create URL for blob
    const blobUrl = window.URL.createObjectURL(res.data);

    // Create anchor element to trigger download
    const link = document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', name + '.' + ext);
    document.body.appendChild(link);

    // Trigger download
    link.click();

    // Clean up
    document.body.removeChild(link);
  });
}

export const getInitials = (name?: string | null) => {
  const eligibleName = name?.replace(/[^a-zA-Z0-9\s]/g, '');
  if (!eligibleName) return '';

  const parts = eligibleName?.trim()?.split(/\s+/);

  const first = parts[0]?.charAt(0)?.toUpperCase() ?? '';
  const last = parts?.length > 1 ? parts[parts.length - 1]?.charAt(0)?.toUpperCase() : '';

  return first + last;
};

export const generateAvatar = (name = '') => `https://ui-avatars.com/api/?background=ffffff&color=003399&&name=${name}`;

export const copyToClipboard = async (str = '', successMessage = 'Phone Number Copied.') => {
  await window.navigator.clipboard.writeText(str).then(() => {
    notification.success({ message: successMessage });
  });
};

// device preferences that are not tied to the signed-in user, kept across logout.
// APP_THEME_KEY is left out on purpose: logout must wipe it so public pages render light.
const PRESERVED_LOCAL_STORAGE_KEYS = [
  LocalStorageKeys.claimNumberSkipped,
  LocalStorageKeys.contactSourceTypeFilter,
  DEVICE_THEME_PREFERENCE_KEY,
];

export function deleteStorages() {
  const deviceId = Cookies.get(COOKIE_KEY.deviceId);
  const preserved = PRESERVED_LOCAL_STORAGE_KEYS.map((key) => [key, localStorage.getItem(key)] as const);

  Object.values(COOKIE_KEY)
    .filter((val) => val !== COOKIE_KEY.locale)
    .map((val) => Cookies.remove(val, cookieOptions));
  localStorage.clear();
  sessionStorage.clear();
  Cookies.set(COOKIE_KEY.deviceId, deviceId + '', {
    path: '/',
    secure: true,
    expires: 365,
  });
  preserved.forEach(([key, value]) => {
    if (value) localStorage.setItem(key, value);
  });
}

export function prefixPlus(phoneNumber = ''): CallPhoneNumber {
  let parsedPhoneNumber;
  if (phoneNumber.includes('+')) {
    parsedPhoneNumber = phoneNumber;
  } else {
    parsedPhoneNumber = '+' + phoneNumber;
  }

  return parsedPhoneNumber as CallPhoneNumber;
}
export const REGEX = {
  check_symbol_number:
    /^(?=.*[0-9])(?=.*[!@#$%^&*()_\-+=[\]{};:'",.<>/?\\|`~])[a-zA-Z\d!@#$%^&*()_\-+=[\]{};:'",.<>/?\\|`~]+$/,
  check_lowerCase_upperCase: /^(?=.*[a-z])(?=.*[A-Z]).+$/,
  company_website: /^(https?:\/\/)?([a-zA-Z0-9-_]+\.)+[a-zA-Z]{2,}(\/.*)?$/,
};

export function formatTimeLeft(minutes?: number, seconds?: number): string {
  const mm = typeof minutes === 'number' ? String(minutes).padStart(2, '0') : '00';
  const ss = typeof seconds === 'number' ? String(seconds).padStart(2, '0') : '00';
  return `${mm}:${ss}`;
}

export const getCookiesAuthToken = (): string | null => {
  const authToken = Cookies.get(COOKIE_KEY.accessToken);
  return authToken ? authToken : null;
};

export const getCookiesWorkspaceSecret = (): string | null => {
  const workspaceSecret = Cookies.get(COOKIE_KEY.workspaceSecret);
  return workspaceSecret ? workspaceSecret : null;
};

export const getCookiesDeviceId = (): string | null => {
  const deviceId = Cookies.get(COOKIE_KEY.deviceId);
  return deviceId ? deviceId : null;
};

export function getElementCenterRelativePosition(
  element: HTMLElement | undefined | null
): { x: number; y: number } | null {
  if (!element) return null;

  const rect = element.getBoundingClientRect();

  return {
    x: (rect.right + rect.left) / 2 / window.innerWidth,
    y: (rect.top + rect.bottom) / 2 / window.innerHeight,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function debounce<T extends (...args: any[]) => void>(cb: T, delay: number = 1000) {
  let timeoutId: ReturnType<typeof setTimeout>;

  return (...args: Parameters<T>) => {
    if (timeoutId) {
      clearInterval(timeoutId);
    }
    timeoutId = setTimeout(() => cb(...args), delay);
  };
}

export class SlidingWindowRateLimiter {
  private timestamps: number[] = [];

  constructor(
    private limit: number,
    private intervalMs: number
  ) {}

  allow(): boolean {
    const now = Date.now();
    // Remove timestamps outside the window
    this.timestamps = this.timestamps.filter((ts) => now - ts <= this.intervalMs);

    if (this.timestamps.length < this.limit) {
      this.timestamps.push(now);
      return true; // Allow action
    }

    return false; // Throttle
  }
}

export function fibonacciSequence(limit = 12) {
  const sequence = [0, 1];

  for (let i = 0; i < limit; i++) {
    sequence.push(sequence[i] + sequence[i + 1]);
  }

  // remove first 2 elements
  sequence.splice(0, 2);

  return sequence;
}

export function formatSecondsToMMSS(seconds: number): string {
  return dayjs.duration(seconds * 1000).format('mm:ss');
}

export const countCodePoints = (value: string): number => [...value].length;

export const hexToRgba = (hex: string, opacity: number): string => {
  const sanitizedHex = hex.replace('#', '');
  const bigint = parseInt(sanitizedHex, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

export function isPublicRoute(pathname: string) {
  const publicRoutes = [...appPublicRoutes, ...basePublicRoutes];
  const isPublicPage = publicRoutes.find((publicRoute) => {
    // Matches dynamic route segments like :token, :id, :slug, etc.
    // e.g. /auth/invitation/:token → replaces `:token` with `[^/]+`
    const dynamicSegmentPattern = /:[^/]+/g;

    // Matches any non-slash characters (a single URL segment)
    // e.g. `abc123`, `xyz-456`, `user_99`
    const wildcardMatcher = '[^/]+';

    // Replace all dynamic segments in the route path with the wildcard matcher
    // e.g. /auth/invitation/:token → /auth/invitation/[^/]+
    const routeWithWildcards = publicRoute.path.replace(dynamicSegmentPattern, wildcardMatcher);

    // Build a full regex that anchors the pattern to the start and end of the pathname
    // (/.*)?$ allows optional trailing segments
    // e.g. /auth/invitation/[^/]+ → /^\/auth\/invitation\/[^/]+(\/.*)?$/
    const routeRegex = new RegExp(`^${routeWithWildcards}(/.*)?$`);

    // Test the actual pathname against the generated regex
    // e.g. /auth/invitation/abc123 → true
    // e.g. /auth/invitation        → false (missing token segment)
    // e.g. /auth/invitationXYZ     → false (no slash before token)
    return routeRegex.test(pathname);
  });

  return isPublicPage;
}

export function getWalletAmountClassName(walletAmountThreshold: { low: boolean; medium: boolean }) {
  switch (true) {
    case walletAmountThreshold?.low:
      return WALLET_AMOUNT_THRESHOLD_COLORS.LOW;

    case walletAmountThreshold?.medium:
      return WALLET_AMOUNT_THRESHOLD_COLORS.MEDIUM;

    default:
      return WALLET_AMOUNT_THRESHOLD_COLORS.HIGH;
  }
}

export function isDifferent(arr1: number[], arr2: number[]) {
  if (arr1.length !== arr2.length) return true;
  const arr1Sorted = [...arr1].sort();
  const arr2Sorted = [...arr2].sort();
  return arr1Sorted.toString() !== arr2Sorted.toString();
}

export const isNumberArray = (value: unknown): value is number[] =>
  Array.isArray(value) && value.every((v) => typeof v === 'number');

export const formatFileSize = (bytes: number) => {
  if (!bytes) return '0 B';
  const i = Math.floor(Math.log(bytes) / Math.log(1024)); // log is used to check how many times 1024 fits into bytes
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${FILE_SIZE[i]}`;
};

export const phoneParser = new PhoneNumberParser();

export const cookieOptions = {
  path: '/',
  domain: cookieSubdomain,
  secure: true,
  expires: 90, // 3 months
};
export const postLogoutAction = () => {
  deleteStorages();
  broadcastAuthChange();
  window.location.href = '/login';
};
